// Imports
import React, { useContext, useEffect, useRef } from 'react';
import { StepNumbers } from '../../app';
import APIConnectedFormContext from '../../../shareables/contexts/api-connected-form/index';


// Define the accepted props
interface Props {
	stepNumber: StepNumbers;
	setStepNumber: React.Dispatch<React.SetStateAction<StepNumbers>>;
	fieldToStepNumberMap: {
		[field: string]: StepNumbers;
	};
	startsWith: {
		[partialField: string]: StepNumbers;
	};
	verificationCodeSetter: React.MutableRefObject<((newValue: string) => void) | undefined>;
	setVerificationCodeExpiredError: React.Dispatch<React.SetStateAction<string>>;
}


// Function component
const MultiStepErrorHandler: React.FC<Props> = ({
	stepNumber,
	setStepNumber,
	fieldToStepNumberMap,
	startsWith,
	verificationCodeSetter,
	setVerificationCodeExpiredError,
}) => {
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Grab error value
	let field: string | null = null;
	let message: string | null = null;
	
	if (context?.error) {
		field = context.error.field;
		message = context.error.message;
	}
	
	
	// Clear the verification code when appropriate
	useEffect(() => {
		if (stepNumber === 2 && field === 'verificationCode') {
			setVerificationCodeExpiredError(message ?? '');
			verificationCodeSetter.current?.('');
		}
	}, [field, message, stepNumber, setVerificationCodeExpiredError, verificationCodeSetter]);
	
	
	// Remember which errored field we last changed steps for
	const handledErrorField = useRef<string | null>(null);
	
	
	// Change to the appropriate step when an error is present
	//  -> Only changes steps once per error, so the user can move to another step while the error is still displayed
	//  -> Depends on the API-connected form clearing errors at the start of every submission, which resets the remembered field so the same field erroring again still moves the form
	useEffect(() => {
		if (!field) {
			handledErrorField.current = null;
			
			return;
		}
		
		if (field === handledErrorField.current) {
			return;
		}
		
		if (Number.isInteger(fieldToStepNumberMap[field])) {
			handledErrorField.current = field;
			setStepNumber(fieldToStepNumberMap[field]);
			
			return;
		}
		
		const startsWithKeys = Object.keys(startsWith);
		const startsWithIndex = startsWithKeys.findIndex((key) => field?.startsWith(key));
		
		if (startsWithIndex !== -1) {
			handledErrorField.current = field;
			setStepNumber(startsWith[startsWithKeys[startsWithIndex]]);
		}
	}, [field, setStepNumber, fieldToStepNumberMap, startsWith]);
	
	
	// Return
	return null;
};

export default MultiStepErrorHandler;
